import { computed } from 'vue';
import { useI18n } from '@/composables/useI18n';

type TranslateFn = (key: string, params?: Record<string, unknown> | unknown[]) => string;

export const buildGuideLinks = (locale: string) => ({
  propeller: `https://docs.bemob.com/${locale}/propellerads-settings`,
  exoclick: `https://docs.bemob.com/${locale}/exoclick-settings`,
  mgid: `https://docs.bemob.com/${locale}/mgid-settings`,
  revcontent: `https://docs.bemob.com/${locale}/revcontent-settings`,
  facebook: `https://docs.bemob.com/${locale}/facebook-settings`,
  trafficjunky: `https://docs.bemob.com/${locale}/trafficjunky-settings`,
});

export const buildIntegrationsData = (
  translate: TranslateFn,
  locale: string,
): Record<string, Integration> => {
  const guideLinks = buildGuideLinks(locale);

  return {
    propeller: {
      name: 'PropellerAds',
      type: 'propeller',
      accessToken: {
        label: translate('API Token') + '*',
        labelHelp: translate('Specify your API key to authenticate the requests of BeMob'),
        placeholder: translate('Type Your {0} API Token', ['Propellerads']),
      },
      img: {
        src: 'propeller.png',
      },
      guideLink: guideLinks.propeller,
      help: translate(
        'Follow these steps to integrate BeMob with PropellerAds for automatic cost collection: <br>- specify the custom name for integration. The name is limited to 255 alphanumeric characters.<br>- copy your API key from PropellerAds account. For this log into <a href="{0}">PropellerAds</a> and select <b>API access</b> in <b>Personal</b> details at the top of the panel.<br>- return to your BeMob settings and paste the copied key value.<br>- click on <b>Create Integration</b>.<br>More details find in our dedicated <a href="{1}">guide</a>.',
        ['https://partners.propellerads.com/#/app/auth', guideLinks.propeller],
      ),
    },
    exoclick: {
      name: 'ExoClick',
      type: 'exoclick',
      accessToken: {
        label: translate('API Token') + '*',
        labelHelp: translate('Specify your API key to authenticate the requests of BeMob'),
        placeholder: translate('Type Your {0} API Token', ['ExoClick']),
      },
      img: {
        src: 'ex-oclick.svg',
        style: {
          height: '20px',
          'margin-top': ' -3px',
        },
      },
      guideLink: guideLinks.exoclick,
      help: translate(
        'Follow these steps to integrate BeMob with ExoClick for automatic cost collection: <br>- specify the custom name for integration. The name is limited to 255 alphanumeric characters.<br>- copy your API key from ExoClick account. For this log into <a href="{0}">ExoClick</a> and access Profile settings at the top of the panel and select API Tokens.<br>- return to your BeMob settings and paste the copied key value.<br>- click on Create Integration.<br>More details find in our dedicated <a href="{1}">guide</a>.',
        ['https://admin.exoclick.com/login', guideLinks.exoclick],
      ),
    },
    mgid: {
      name: 'MGID',
      type: 'mgid',
      clientId: {
        label: `${translate('Client ID')} (${translate('API ID')})*`,
        placeholder: translate('API ID'),
      },
      accessToken: {
        label: translate('API Token') + '*',
        labelHelp: translate('Specify your API key to authenticate the requests of BeMob'),
        placeholder: translate('Enter the MGID access token'),
      },
      img: {
        src: 'mgid.svg',
        style: {
          height: '34px',
        },
      },
      guideLink: guideLinks.mgid,
      help: translate(
        'Follow these steps to integrate BeMob with MGID for automatic cost collection: <br>- specify the custom name for integration. The name is limited to 255 alphanumeric characters.<br>- copy your API key from MGID account. For this log into <a href="{0}">MGID account</a> and go to <b>My Profile</b> → <b>Users</b> →<b>Get your token</b>.<br>- return to your BeMob settings and paste the copied key value.<br>- click on <b>Create Integration</b>.<br>More details find in our dedicated <a href="{1}">guide</a>.',
        ['https://dashboard.mgid.com/user/signin', guideLinks.mgid],
      ),
    },
    revcontent: {
      name: 'Revcontent',
      type: 'revcontent',
      clientId: {
        label: translate('Client ID') + '*',
        placeholder: translate('Client ID'),
      },
      accessToken: {
        label: translate('API Token') + '*',
        labelHelp: translate('Specify your API key to authenticate the requests of BeMob'),
        placeholder: translate('Type Your {0} API Token', ['Revcontent']),
      },
      img: {
        src: 'revc-ontent.svg',
        style: {
          height: '20px',
        },
      },
      guideLink: guideLinks.revcontent,
      help: translate(
        'Follow these steps to integrate BeMob with Revcontent for automatic cost collection: <br>- specify the custom name for integration. The name is limited to 255 alphanumeric characters.<br>- copy your API key from Revcontent account. For this log into <a href="{0}">Revcontent</a> and request the API access from the support.<br>After that access the Account Settings and scroll down to see the API key<br>- return to your BeMob settings and paste the copied key value.<br>- click on <b>Create Integration</b>.<br>More details find in our dedicated <a href="{1}">guide</a>.',
        ['https://www.revcontent.com/login', guideLinks.revcontent],
      ),
    },
    'facebook-conversions': {
      name: 'Facebook',
      type: 'facebook-conversions',
      clientId: {
        label: `${translate('Client ID')} (${translate('Pixel ID')})*`,
        labelHelp: translate(
          "Access your Facebook Pixel's setting page → Details section → copy pixel ID",
        ),
        placeholder: translate('Specify your Facebook Pixel ID'),
      },
      accessToken: {
        label: translate('API Key') + '*',
        placeholder: translate('Enter your Facebook API Key'),
        labelHelp: translate(
          'Go to Facebook Pixel settings → scroll down to the Conversion API section →  click the Generate Access Token within Set up manually section',
        ),
      },
      img: {
        src: 'facebook.svg',
        style: {
          height: '20px',
        },
      },
      guideLink: guideLinks.facebook,
      help: translate(
        'With this integration enabled the conversions data will be passed to Facebook side.<br>Follow these steps to set up the integration:<br>1. Specify the custom integration name. The integration will be displayed with this name in Traffic Source settings.<br>2. Find and copy your Pixel ID and Conversion API token on the side of Facebook.<br>3. Specify them in the corresponding fields below.<br>4. Verify your domain on the side of Facebook.<br>5. Paste it in the <b>Event Source URL</b> field.<br>6. Click the <b>Create Integration</b> button.<br>7. If credentials are correct, the settings will be saved.<br>8. Go to the <b>Traffic Source</b> tab to select <b>Facebook</b> in the Integration section.<br><a href={0} target="_blank">Check our step-by-step guide to learn more.</a>',
        [`https://docs.bemob.com/${locale}/facebook-settings`],
      ),
    },
    trafficjunky: {
      name: 'TrafficJunky',
      type: 'trafficjunky',
      accessToken: {
        label: translate('API Token') + '*',
        labelHelp: translate('Specify your API key to authenticate the requests from BeMob.'),
        placeholder: translate('Type Your TrafficJunky Token'),
      },
      img: {
        src: 'trafficjunky.svg',
      },
      guideLink: guideLinks.trafficjunky,
      help: translate(
        'Follow these steps to integrate BeMob with <b>TrafficJunky</b> for automatic cost collection: <br>- specify the custom name for integration. The name is limited to 255 alphanumeric characters.<br>- copy your API key from <b>TrafficJunky</b>. For this log into <a href="{0}">TrafficJunky account</a> and click on <b>Generate New Token</b> in <b>My Profile</b> settings in the bottom left corner of the panel. <br>- return to your BeMob settings and paste the copied key value.<br>- click on <b>Create Integration</b>.<br>More details find in our dedicated <a href="{1}">guide</a>.',
        ['https://advertiser.trafficjunky.com/my-profile-account', guideLinks.trafficjunky],
      ),
    },
  };
};

export const useIntegrations = () => {
  const { t, locale } = useI18n();

  const guideLinks = computed(() => buildGuideLinks(String(locale.value)));
  const integrationsData = computed(() => buildIntegrationsData(t, String(locale.value)));

  return { guideLinks, integrationsData };
};
